import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../utils/hooks/useFacility";
import { GET_COURT_GRID_SCHEDULE } from "../../../api/apiRoutes";
import { State } from "../../../store";
import { CourtGridData } from "../../../models/CourtGridData";

interface UseCourtDataProps {
  selectedDate: Date;
  selectedCourts: string[];
  exerciseName?: string;
}

export default function useCourtData({ selectedDate, selectedCourts, exerciseName }: UseCourtDataProps) {
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const userData = useSelector((state: State) => state.auth.userData);
  const isMountedRef = useRef(true);

  const [courtGridData, setCourtGridData] = useState<CourtGridData | null>(null);
  const [loading, setLoading] = useState(false);

  const requestCourtGridSchedule = async () => {
    if (!facilityIds || !isMountedRef.current) return;

    if (isMountedRef.current) {
      setLoading(true);
    }

    const formattedDate = format(selectedDate, "yyyy-MM-dd");

    let courtNames = "";
    if (exerciseName) {
      courtNames = exerciseName;
    } else if (selectedCourts.length > 0) {
      courtNames = selectedCourts.join(";");
    }

    const userIds = userData?.objectId ? `0,${userData.objectId}` : undefined;

    const { result, isError } = await get(GET_COURT_GRID_SCHEDULE(facilityIds, formattedDate, courtNames, userIds));

    // Sprawdź czy komponent jest nadal zamontowany przed aktualizacją stanu
    if (!isMountedRef.current) return;

    if (!isError) {
      setCourtGridData(result);
    }

    setLoading(false);
  };

  useEffect(() => {
    requestCourtGridSchedule();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, facilityIds, ...(exerciseName ? [] : [selectedCourts])]);

  // Cleanup function - oznacz komponent jako odmontowany
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    courtGridData,
    loading,
    refetch: requestCourtGridSchedule,
  };
}
